/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import DOMPurify from "dompurify";

const ALLOWED_IFRAME_HOSTS = [
  "youtube.com",
  "youtube-nocookie.com",
  "youtu.be",
  "vimeo.com",
  "player.vimeo.com",
  "dailymotion.com",
  "loom.com",
];

const isAllowedIframeSrc = (src) => {
  if (!src) return false;
  try {
    const { hostname, protocol } = new URL(src, window.location.href);
    if (protocol !== "http:" && protocol !== "https:") return false;
    const host = hostname.toLowerCase();

    return ALLOWED_IFRAME_HOSTS.some(
      (allowed) => host === allowed || host.endsWith(`.${allowed}`)
    );
  } catch (e) {
    return false;
  }
};

export const isSafeRedirectUrl = (url) => {
  if (!url || typeof url !== "string") return false;
  try {
    const { protocol } = new URL(url, window.location.href);
    return protocol === "http:" || protocol === "https:";
  } catch (e) {
    return false;
  }
};

DOMPurify.addHook("uponSanitizeElement", (node, data) => {
  if (data.tagName === "iframe" && !isAllowedIframeSrc(node.getAttribute("src"))) {
    node.parentNode?.removeChild(node);
  }
});

const SANITIZE_CONFIG = {
  ADD_TAGS: ["iframe"],
  ADD_ATTR: ["allow", "allowfullscreen", "frameborder", "scrolling"],
};

export const sanitizeHtml = (html) => DOMPurify.sanitize(html ?? "", SANITIZE_CONFIG);

export const htmlToPlainText = (html) => {
  if (!html) return "";

  let current = String(html);

  for (let pass = 0; pass < 5; pass++) {
    const spaced = current
      .replace(/<\/(p|div|li|h[1-6]|tr)>/gi, " ")
      .replace(/<br\s*\/?>/gi, " ");

    const el = document.createElement("div");
    el.innerHTML = sanitizeHtml(spaced);
    const text = el.textContent || "";

    if (text === current) break;
    current = text;
  }

  return current.replace(/\s+/g, " ").trim();
};
